import React from "react";

const MATCH_WIDTH = 100;

type Pairing = {
    player1_id: string | null;
    player2_id: string | null;
};

type TournamentBracketTreeProps = {
    pairings: Pairing[];
    playerDisplay: (playerId: string | null) => React.ReactNode;
};

export function TournamentBracketTree({ pairings, playerDisplay }: TournamentBracketTreeProps) {
    const numPairings = pairings.length;
    const numRounds = Math.ceil(Math.log2(numPairings * 2));
    const bracketSize = Math.pow(2, numRounds);

    if (![4, 8, 16].includes(bracketSize)) {
        return (
            <div className="text-sm text-gray-600">
                Bracket visualization only supports 4, 8 or player tournaments
            </div>
        );
    }

    const config = getBracketConfig(bracketSize);
    const allMatches = generateAllMatches(pairings, config);
    const containerWidth = 1024;
    const matchPositionsByRound = calculateMatchPositions(allMatches, config, containerWidth);

    return (
        <div
            className="flex flex-col items-start gap-4 py-4 text-xs"
            style={{ width: containerWidth }}
        >
            {config.matchesPerRound.map((_, roundIndex) => {
                const roundPositions = matchPositionsByRound[roundIndex] || [];
                const hasNextRound = roundIndex < config.rounds - 1;

                const matchPositionsWithNext = hasNextRound
                    ? roundPositions.map((pos, posIndex) => {
                          const nextRoundPositions = matchPositionsByRound[roundIndex + 1] || [];
                          const nextMatchIndex = Math.floor(posIndex / 2);
                          const nextMatch = nextRoundPositions[nextMatchIndex];

                          return {
                              x: pos.xPos,
                              nextX: nextMatch?.xPos,
                          };
                      })
                    : [];

                return (
                    <React.Fragment key={roundIndex}>
                        <div className="relative" style={{ width: containerWidth, height: 120 }}>
                            {roundPositions.map(({ match, xPos }, matchIndex) => (
                                <div
                                    key={matchIndex}
                                    className={`absolute rounded border px-2 py-2 ${getRoundStyle(roundIndex, config.rounds)}`}
                                    style={{
                                        left: xPos - MATCH_WIDTH / 2,
                                        width: MATCH_WIDTH,
                                        height: "100%",
                                    }}
                                >
                                    <div className="mb-1 text-center text-xs font-medium">
                                        {getRoundTitle(roundIndex, matchIndex, config.rounds)}
                                    </div>
                                    <div className="text-center text-xs">
                                        {roundIndex === 0 ? (
                                            <>
                                                <div className="truncate">
                                                    {match.player1_id
                                                        ? playerDisplay(match.player1_id)
                                                        : "TBD"}
                                                </div>
                                                <div className="my-1 text-xs text-gray-400">vs</div>
                                                <div className="truncate">
                                                    {match.player2_id
                                                        ? playerDisplay(match.player2_id)
                                                        : "TBD"}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="mt-1 text-center text-gray-500">
                                                {getAdvancementText(
                                                    roundIndex,
                                                    matchIndex,
                                                    config.rounds
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {hasNextRound && (
                            <div className="relative" style={{ width: containerWidth, height: 32 }}>
                                <BracketConnectors
                                    matchPositions={matchPositionsWithNext}
                                    containerWidth={containerWidth}
                                />
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

type BracketConfig = {
    rounds: number;
    matchesPerRound: number[];
};

type MatchWithPosition = {
    match: Pairing;
    xPos: number;
    nextMatchId?: string;
};

const calculateMatchPositions = (
    allMatches: Pairing[][],
    config: BracketConfig,
    containerWidth: number = 1200
) => {
    const matchPositionsByRound: Record<number, MatchWithPosition[]> = {};
    const matchSpacing = 20;

    const firstRoundMatches = allMatches[0] || [];
    const firstRoundOffset = matchSpacing;

    matchPositionsByRound[0] = firstRoundMatches.map((match, index) => ({
        match,
        xPos: firstRoundOffset + index * (MATCH_WIDTH + matchSpacing) + MATCH_WIDTH / 2,
        nextMatchId: `match-${1}-${Math.floor(index / 2)}`,
    }));

    for (let roundIndex = 1; roundIndex < config.rounds; roundIndex++) {
        const roundMatches = allMatches[roundIndex] || [];
        const prevRoundPositions = matchPositionsByRound[roundIndex - 1];

        matchPositionsByRound[roundIndex] = roundMatches.map((match, matchIndex) => {
            const sourcePositions = prevRoundPositions
                .filter((_, sourceIndex) => Math.floor(sourceIndex / 2) === matchIndex)
                .map(pos => pos.xPos);

            const midpointOfSourceMatches =
                sourcePositions.length > 0
                    ? sourcePositions.reduce((sum, x) => sum + x, 0) / sourcePositions.length
                    : containerWidth / 2;

            return {
                match,
                xPos: midpointOfSourceMatches,
                nextMatchId:
                    roundIndex < config.rounds - 1
                        ? `match-${roundIndex + 1}-${Math.floor(matchIndex / 2)}`
                        : undefined,
            };
        });
    }

    return matchPositionsByRound;
};

const BracketConnectors: React.FC<{
    matchPositions: { x: number; nextX?: number }[];
    containerWidth: number;
}> = ({ matchPositions, containerWidth }) => {
    const connectors = [];
    const curvedCornerRadius = 8;
    const verticalSpacing = 32;

    for (let i = 0; i < matchPositions.length; i += 2) {
        if (i + 1 < matchPositions.length && matchPositions[i].nextX !== undefined) {
            const leftX = matchPositions[i].x;
            const rightX = matchPositions[i + 1].x;
            const targetX = matchPositions[i].nextX!;
            const midY = verticalSpacing / 2;

            // Create curved path for left match connection
            const leftPath = `
                M ${leftX} 0
                L ${leftX} ${midY - curvedCornerRadius}
                Q ${leftX} ${midY} ${leftX < targetX ? leftX + curvedCornerRadius : leftX - curvedCornerRadius} ${midY}
                L ${targetX} ${midY}
            `;

            // Create curved path for right match connection
            const rightPath = `
                M ${rightX} 0
                L ${rightX} ${midY - curvedCornerRadius}
                Q ${rightX} ${midY} ${rightX > targetX ? rightX - curvedCornerRadius : rightX + curvedCornerRadius} ${midY}
                L ${targetX} ${midY}
            `;

            connectors.push(
                <svg
                    key={`connector-${i / 2}`}
                    className="absolute inset-0 h-full w-full"
                    viewBox={`0 0 ${containerWidth} ${verticalSpacing}`}
                    preserveAspectRatio="none"
                >
                    {/* Left match connection */}
                    <path d={leftPath} stroke="#374151" strokeWidth="2" fill="none" />

                    {/* Right match connection */}
                    <path d={rightPath} stroke="#374151" strokeWidth="2" fill="none" />

                    {/* Vertical line to next round */}
                    <line
                        x1={targetX}
                        y1={verticalSpacing / 2}
                        x2={targetX}
                        y2={verticalSpacing}
                        stroke="#374151"
                        strokeWidth="2"
                    />
                </svg>
            );
        }
    }

    return <>{connectors}</>;
};

function getBracketConfig(bracketSize: number): BracketConfig {
    const rounds = Math.log2(bracketSize);
    const matchesPerRound = Array.from(
        { length: rounds },
        (_, i) => bracketSize / Math.pow(2, i + 1)
    );

    return {
        rounds,
        matchesPerRound,
    };
}

function generateAllMatches(pairings: Pairing[], config: BracketConfig): Pairing[][] {
    const rounds: Pairing[][] = [];

    const firstRound: Pairing[] = [];
    for (let i = 0; i < config.matchesPerRound[0]; i++) {
        if (i < pairings.length) {
            firstRound.push(pairings[i]);
        } else {
            firstRound.push({ player1_id: null, player2_id: null });
        }
    }
    rounds.push(firstRound);

    for (let round = 1; round < config.rounds; round++) {
        const roundMatches: Pairing[] = [];
        for (let match = 0; match < config.matchesPerRound[round]; match++) {
            roundMatches.push({ player1_id: null, player2_id: null });
        }
        rounds.push(roundMatches);
    }

    return rounds;
}

function getRoundStyle(roundIndex: number, totalRounds: number): string {
    if (roundIndex === totalRounds - 1) {
        return "border-2 border-yellow-400 bg-yellow-50";
    } else if (roundIndex === 0) {
        return "bg-white";
    } else {
        return "bg-gray-50";
    }
}

function getRoundTitle(roundIndex: number, matchIndex: number, totalRounds: number): string {
    if (roundIndex === totalRounds - 1) {
        return "Final";
    } else if (roundIndex === totalRounds - 2) {
        return `Semifinal ${matchIndex + 1}`;
    } else if (roundIndex === totalRounds - 3) {
        return `Quarterfinal ${matchIndex + 1}`;
    } else if (roundIndex === totalRounds - 4) {
        return `R16-${matchIndex + 1}`;
    } else {
        return `M${matchIndex + 1}`;
    }
}

function getAdvancementText(roundIndex: number, matchIndex: number, totalRounds: number): string {
    if (roundIndex === totalRounds - 1) {
        // Final
        if (totalRounds === 2) {
            return "Winner M1 vs Winner M2";
        } else if (totalRounds === 3) {
            return "Winner SF1 vs Winner SF2";
        } else if (totalRounds === 4) {
            return "Winner SF1 vs Winner SF2";
        } else if (totalRounds === 5) {
            return "Winner SF1 vs Winner SF2";
        }
    } else if (roundIndex === totalRounds - 2) {
        // Semifinals
        if (totalRounds === 3) {
            return matchIndex === 0 ? "Winner M1 vs Winner M2" : "Winner M3 vs Winner M4";
        } else if (totalRounds === 4) {
            return matchIndex === 0 ? "Winner QF1 vs Winner QF2" : "Winner QF3 vs Winner QF4";
        } else if (totalRounds === 5) {
            return matchIndex === 0 ? "Winner QF1 vs Winner QF2" : "Winner QF3 vs Winner QF4";
        }
    } else if (roundIndex === totalRounds - 3) {
        // Quarterfinals
        if (totalRounds === 4) {
            return `W${matchIndex * 2 + 1} vs W${matchIndex * 2 + 2}`;
        } else if (totalRounds === 5) {
            return matchIndex === 0
                ? "Winner R16-1 vs Winner R16-2"
                : matchIndex === 1
                  ? "Winner R16-3 vs Winner R16-4"
                  : matchIndex === 2
                    ? "Winner R16-5 vs Winner R16-6"
                    : "Winner R16-7 vs Winner R16-8";
        }
    } else if (roundIndex === totalRounds - 4) {
        // Round of 16 (only for 32-player)
        return `W${matchIndex * 2 + 1} vs W${matchIndex * 2 + 2}`;
    }
    return "";
}
